/**
 * Catalogue import — upload a spreadsheet, check the column mapping, import.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react';

export const ShopImportDialog = ({ open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setPreview(null); setMapping({}); setResult(null); };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/shop/admin/import/preview', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setMapping(res.data.mapping || {});
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = useMutation({
    mutationFn: async () => (await api.post('/shop/admin/import/commit', {
      mapping, rows: preview.rows, publish,
    })).data,
    onSuccess: (data) => {
      setResult(data);
      toast.success(`${data.created} added, ${data.updated} updated`);
      queryClient.invalidateQueries({ queryKey: ['shop-admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['shop-admin-dashboard'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/shop/admin/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ministry-shop-import.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl" data-testid="shop-import-dialog">
        <DialogHeader>
          <DialogTitle>Import your catalogue</DialogTitle>
          <DialogDescription>
            A spreadsheet saved as CSV. We match on SKU, then name — so
            re-importing an updated sheet updates instead of duplicating.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[68vh] overflow-y-auto pr-1">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex">
              <input type="file" accept=".csv,text/csv" className="hidden"
                     data-testid="shop-import-file"
                     onChange={(e) => upload(e.target.files?.[0])} />
              <Button asChild variant="outline" size="sm" disabled={busy}>
                <span>
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                  Choose a CSV
                </span>
              </Button>
            </label>
            <Button variant="ghost" size="sm" onClick={downloadTemplate}
                    data-testid="shop-import-template">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Download the template
            </Button>
          </div>

          {preview && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="secondary" data-testid="shop-import-rowcount">
                  {preview.total_rows} rows
                </Badge>
                {preview.missing_required.length > 0 && (
                  <Badge variant="outline" className="border-amber-300 text-amber-700">
                    still needs: {preview.missing_required.join(', ')}
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Which column is which</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {preview.headers.map((header) => (
                    <div key={header} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-32 truncate"
                            title={header}>{header}</span>
                      <Select value={mapping[header] || 'ignore'}
                              onValueChange={(v) => setMapping((m) => {
                                const next = { ...m };
                                if (v === 'ignore') delete next[header];
                                else next[header] = v;
                                return next;
                              })}>
                        <SelectTrigger className="h-8 text-xs"
                                       data-testid={`shop-import-map-${header}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ignore">Ignore this column</SelectItem>
                          {preview.fields.map((f) => (
                            <SelectItem key={f.key} value={f.key}>
                              {f.label}{f.required ? ' *' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">First few rows as they will import</Label>
                <div className="border border-slate-200 divide-y divide-slate-100">
                  {preview.preview.map((row, index) => (
                    <div key={index} className="p-3 text-xs flex flex-wrap gap-2 items-center"
                         data-testid={`shop-import-preview-${index}`}>
                      <Badge variant={row.action === 'create' ? 'secondary' : 'outline'}
                             className="text-[10px]">{row.action}</Badge>
                      <span className="font-medium">{row.shaped.name || '(no name)'}</span>
                      <span className="text-slate-500">
                        {row.shaped.category} · {row.shaped.kind} · {row.shaped.price}
                        {row.shaped.stock ? ` · ${row.shaped.stock} in stock` : ''}
                      </span>
                      {row.warnings.map((w) => (
                        <span key={w} className="text-amber-700">⚠ {w}</span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch checked={publish} data-testid="shop-import-publish"
                        onCheckedChange={setPublish} />
                <span className="text-sm text-slate-600">
                  Put them live straight away (rows with no price stay as drafts)
                </span>
              </div>
            </>
          )}

          {result && (
            <div className="border border-emerald-200 bg-emerald-50 p-4 text-sm"
                 data-testid="shop-import-result">
              <p className="font-medium text-emerald-800">
                {result.created} added · {result.updated} updated · {result.skipped} skipped
              </p>
              {(result.errors || []).map((e) => (
                <p key={e.row} className="text-xs text-amber-700 mt-1">
                  Row {e.row}: {e.error}
                </p>
              ))}
            </div>
          )}

          {!preview && !busy && (
            <div className="border border-dashed border-slate-200 p-10 text-center">
              <FileSpreadsheet className="h-7 w-7 mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                Choose a CSV to see exactly what will be imported before anything changes.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button disabled={!preview || commit.isPending} data-testid="shop-import-commit"
                  onClick={() => commit.mutate()}>
            {commit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Import {preview?.total_rows || 0} rows
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShopImportDialog;
